import {
  calendarDateFromTimestamp,
  extractInvoiceNumberSuffix,
  formatCalendarDate,
  formatNumberToEuroCurrency,
  fromCalendarDate,
  getContactNameFromObject,
  getInvoiceNumberSequenceRange,
  getInvoiceStatusIdWithColor,
  getPeppolError,
  InvoiceStatus,
  InvoiceType,
  isAfter,
  isBefore,
  isNilOrEmpty,
  isTransactionToReconcile,
  isPeppolRegistrationUnverified,
  PeppolErrorType,
  PostingType,
  roundAtSecondDecimal,
  toCalendarDate,
  today,
  TransactionOrigin,
  type CalendarDate,
  type Contact,
  type Integration,
  type Invoice,
  type InvoiceStatusId,
  type InvoiceWithPostings,
  type Language,
  type TransactionWithoutPostings,
  VERIFIABLE_PEPPOL_ERRORS,
  withVerifiedRegistration,
} from '@rentguru/commons-utils';
import { partition } from 'lodash';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

export const AMOUNT_CELL_CLASSES = 'block w-full text-right';
export const UNKNOWN_AMOUNT_PLACEHOLDER = '—';

export interface InvoiceItem {
  id: string;
  ownerId: string;
  customerName: string;
  invoiceDate: string;
  dueDate?: string;
  taxExcludedAmount: number;
  totalAmount: number;
  status: InvoiceStatus;
  paid: boolean;
  paymentStatusId: InvoiceStatusId;
  searchableString: string;
  invoice: InvoiceWithPostings;
  postingsLoading?: boolean;
  postingsUnavailable?: boolean;
}

export enum InvoiceTableSortOptions {
  AMOUNT_ASC,
  AMOUNT_DESC,
  ID_ASC,
  ID_DESC,
  CUSTOMER_NAME_ASC,
  CUSTOMER_NAME_DESC,
  INVOICE_DATE_ASC,
  INVOICE_DATE_DESC,
  DUE_DATE_ASC,
  DUE_DATE_DESC,
  TAX_EXCLUDED_ASC,
  TAX_EXCLUDED_DESC,
}

export const hasNoVisibleDate = (invoice: InvoiceItem): boolean =>
  invoice.status === InvoiceStatus.DRAFT &&
  isNilOrEmpty(invoice.invoice.invoiceNumber) &&
  invoice.invoiceDate === fromCalendarDate(today());

const INVOICE_NUMBER_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const isUnnumberedDraft = (invoice: InvoiceItem): boolean =>
  invoice.status === InvoiceStatus.DRAFT && isNilOrEmpty(invoice.invoice.invoiceNumber);

/** Ascending order: invoice date, then invoice number, with unnumbered drafts after every numbered invoice. */
export const compareInvoicesByDate = (a: InvoiceItem, b: InvoiceItem): number => {
  const aIsUnnumberedDraft = isUnnumberedDraft(a);
  if (aIsUnnumberedDraft !== isUnnumberedDraft(b)) {
    return aIsUnnumberedDraft ? 1 : -1;
  }
  const dateComparison = a.invoiceDate.localeCompare(b.invoiceDate);
  if (dateComparison !== 0) {
    return dateComparison;
  }
  return INVOICE_NUMBER_COLLATOR.compare(a.invoice.invoiceNumber ?? '', b.invoice.invoiceNumber ?? '');
};

export const sortInvoices = (invoices: InvoiceItem[], sortOption: InvoiceTableSortOptions): InvoiceItem[] => {
  switch (sortOption) {
    case InvoiceTableSortOptions.AMOUNT_ASC:
      return orderBy(invoices, (invoice) => invoice.totalAmount, ['asc']);
    case InvoiceTableSortOptions.AMOUNT_DESC:
      return orderBy(invoices, (invoice) => invoice.totalAmount, ['desc']);
    case InvoiceTableSortOptions.ID_ASC: {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      return invoices.sort((a, b) => collator.compare(a.invoice.invoiceNumber ?? '', b.invoice.invoiceNumber ?? ''));
    }
    case InvoiceTableSortOptions.ID_DESC: {
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      return invoices.sort((a, b) => collator.compare(b.invoice.invoiceNumber ?? '', a.invoice.invoiceNumber ?? ''));
    }
    case InvoiceTableSortOptions.CUSTOMER_NAME_ASC: {
      const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
      return invoices.sort((a, b) => collator.compare(a.customerName, b.customerName));
    }
    case InvoiceTableSortOptions.CUSTOMER_NAME_DESC: {
      const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
      return invoices.sort((a, b) => collator.compare(b.customerName, a.customerName));
    }
    case InvoiceTableSortOptions.INVOICE_DATE_ASC:
      return invoices.sort(compareInvoicesByDate);
    case InvoiceTableSortOptions.INVOICE_DATE_DESC:
      return invoices.sort((a, b) => compareInvoicesByDate(b, a));
    case InvoiceTableSortOptions.DUE_DATE_ASC: {
      const [noDateInvoices, rest] = partition(invoices, hasNoVisibleDate);
      return [...noDateInvoices, ...orderBy(rest, (invoice) => invoice.dueDate ?? '', ['asc'])];
    }
    case InvoiceTableSortOptions.DUE_DATE_DESC: {
      const [noDateInvoices, rest] = partition(invoices, hasNoVisibleDate);
      return [...noDateInvoices, ...orderBy(rest, (invoice) => invoice.dueDate ?? '', ['desc'])];
    }
    case InvoiceTableSortOptions.TAX_EXCLUDED_ASC:
      return orderBy(invoices, (invoice) => invoice.taxExcludedAmount, ['asc']);
    case InvoiceTableSortOptions.TAX_EXCLUDED_DESC:
      return orderBy(invoices, (invoice) => invoice.taxExcludedAmount, ['desc']);
    default:
      return invoices;
  }
};

export enum InvoiceGroupByOption {
  CUSTOMER,
  STATUS,
  PAYMENT_STATUS,
  INVOICE_MONTH,
  DUE_MONTH,
}

export enum InvoiceDatePreset {
  THIS_MONTH,
  THIS_QUARTER,
  THIS_YEAR,
}

export interface InvoiceDateFilter {
  preset: InvoiceDatePreset | null;
  from?: CalendarDate;
  to?: CalendarDate;
}

export const EMPTY_INVOICE_DATE_FILTER: InvoiceDateFilter = { preset: null };

const MONTHS_PER_QUARTER = 3;

const getQuarterIndex = (date: CalendarDate): number => Math.floor((date.month - 1) / MONTHS_PER_QUARTER);

export const isInvoiceDateFilterActive = (filter: InvoiceDateFilter): boolean =>
  !isNil(filter.preset) || !isNil(filter.from) || !isNil(filter.to);

export const matchesInvoiceDateFilter = (invoiceDate: string, filter: InvoiceDateFilter): boolean => {
  const date = toCalendarDate(invoiceDate);

  if (!isNil(filter.preset)) {
    const currentDate = today();
    switch (filter.preset) {
      case InvoiceDatePreset.THIS_MONTH:
        return date.year === currentDate.year && date.month === currentDate.month;
      case InvoiceDatePreset.THIS_QUARTER:
        return date.year === currentDate.year && getQuarterIndex(date) === getQuarterIndex(currentDate);
      case InvoiceDatePreset.THIS_YEAR:
        return date.year === currentDate.year;
      default:
        return true;
    }
  }

  if (!isNil(filter.from) && isBefore(date, filter.from)) {
    return false;
  }
  if (!isNil(filter.to) && isAfter(date, filter.to)) {
    return false;
  }
  return true;
};

export interface InvoiceFilters {
  nameFilter: string;
  statusFilter: Set<InvoiceStatus>;
  paymentStatusFilter: Set<InvoiceStatusId>;
  customerFilter: Set<string>;
  dateFilter: InvoiceDateFilter;
}

export const areInvoiceFiltersActive = (filters: InvoiceFilters): boolean =>
  !isNilOrEmpty(filters.nameFilter) ||
  !isEmpty(filters.statusFilter) ||
  !isEmpty(filters.paymentStatusFilter) ||
  !isEmpty(filters.customerFilter) ||
  isInvoiceDateFilterActive(filters.dateFilter);

export const matchesInvoiceFilters = (item: InvoiceItem, filters: InvoiceFilters): boolean => {
  if (!item.searchableString.includes(filters.nameFilter.toLowerCase())) {
    return false;
  }
  if (filters.statusFilter.size > 0 && !filters.statusFilter.has(item.status)) {
    return false;
  }
  if (filters.paymentStatusFilter.size > 0 && !filters.paymentStatusFilter.has(item.paymentStatusId)) {
    return false;
  }
  if (filters.customerFilter.size > 0 && !filters.customerFilter.has(item.ownerId)) {
    return false;
  }
  if (
    isInvoiceDateFilterActive(filters.dateFilter) &&
    (hasNoVisibleDate(item) || !matchesInvoiceDateFilter(item.invoiceDate, filters.dateFilter))
  ) {
    return false;
  }
  return true;
};

const getTaxExcludedAmount = (invoice: InvoiceWithPostings): number => {
  const postingsTaxExcludedSum = invoice.postings
    .filter((posting) => posting.type === PostingType.CREDIT)
    .reduce((sum, posting) => sum + (posting.amountVatExcluded ?? 0), 0);
  return roundAtSecondDecimal(invoice.creditNote ? -postingsTaxExcludedSum : postingsTaxExcludedSum);
};

export const getNextAvailableInvoiceNumber = (
  postedInvoices: Invoice[],
  senderId: string,
  conflictingNumber: string,
  schema: string
): string => {
  const { start, width, afterSequenceRenderedLength } = getInvoiceNumberSequenceRange(schema);
  if (start < 0 || width === 0) {
    return conflictingNumber;
  }

  const prefix = conflictingNumber.substring(0, start);

  const maxSequenceDigitsEnd = conflictingNumber.length - afterSequenceRenderedLength;
  let sequenceDigitsEnd = start;
  while (sequenceDigitsEnd < maxSequenceDigitsEnd && /\d/.test(conflictingNumber[sequenceDigitsEnd])) {
    sequenceDigitsEnd += 1;
  }
  const trailing = conflictingNumber.substring(sequenceDigitsEnd);

  const maxSuffix = postedInvoices.reduce((max, invoice) => {
    const { invoiceNumber } = invoice;
    if (
      invoice.invoiceSenderId !== senderId ||
      isNil(invoiceNumber) ||
      !invoiceNumber.startsWith(prefix) ||
      !invoiceNumber.endsWith(trailing)
    ) {
      return max;
    }
    const suffix = extractInvoiceNumberSuffix(invoiceNumber, schema);
    return suffix === 0 ? max : Math.max(max, suffix);
  }, 0);

  return `${prefix}${String(maxSuffix + 1).padStart(width, '0')}${trailing}`;
};

const MAX_LISTED_DELETED_NUMBERS = 5;

export const getDeleteNumberGapWarningValues = (drafts: Pick<Invoice, 'invoiceNumber'>[]) => {
  const numbers = compact(drafts.map(({ invoiceNumber }) => invoiceNumber));
  if (isEmpty(numbers)) {
    return null;
  }
  const listedNumbers = numbers.slice(0, MAX_LISTED_DELETED_NUMBERS);
  return {
    count: numbers.length,
    numbers: listedNumbers.join(', '),
    moreCount: numbers.length - listedNumbers.length,
  };
};

export interface InvoiceNumberToValidate {
  invoiceId: string;
  invoiceSenderId: string;
  invoiceNumber: string | null | undefined;
  invoiceNumberFormat: string;
  wasSentBefore: boolean;
}

export const getNextNumberIfInvoiceNumberTaken = (
  companyInvoices: Invoice[],
  { invoiceId, invoiceSenderId, invoiceNumber, invoiceNumberFormat, wasSentBefore }: InvoiceNumberToValidate
): string | null => {
  if (isNilOrEmpty(invoiceNumber) || wasSentBefore) {
    return null;
  }
  const postedInvoices = companyInvoices.filter(
    (otherInvoice) => otherInvoice.id !== invoiceId && otherInvoice.status !== InvoiceStatus.DRAFT
  );
  const isTaken = postedInvoices.some(
    (otherInvoice) => otherInvoice.invoiceSenderId === invoiceSenderId && otherInvoice.invoiceNumber === invoiceNumber
  );
  return isTaken
    ? getNextAvailableInvoiceNumber(postedInvoices, invoiceSenderId, invoiceNumber, invoiceNumberFormat)
    : null;
};

export const mapInvoiceToInvoiceItem = (
  invoice: InvoiceWithPostings,
  loadingPostingIds?: Set<string>,
  failedPostingIds?: Set<string>
): InvoiceItem => {
  const customerName = getContactNameFromObject(invoice.owner);
  const { id: paymentStatusId } = getInvoiceStatusIdWithColor(invoice);
  return {
    id: invoice.id,
    ownerId: invoice.ownerId ?? '',
    customerName,
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate ?? undefined,
    taxExcludedAmount: getTaxExcludedAmount(invoice),
    totalAmount: invoice.creditNote ? -invoice.amount : invoice.amount,
    status: invoice.status,
    paid: invoice.paid,
    paymentStatusId,
    invoice,
    searchableString: [invoice.id, invoice.invoiceNumber, customerName, invoice.amount].join(' ').toLowerCase(),
    postingsLoading: loadingPostingIds?.has(invoice.id) ?? false,
    postingsUnavailable: (loadingPostingIds?.has(invoice.id) || failedPostingIds?.has(invoice.id)) ?? false,
  };
};

const PAYABLE_COMPANY_INVOICE_STATUSES: InvoiceStatus[] = [InvoiceStatus.POSTED, InvoiceStatus.INVOICED];

export const canMarkCompanyInvoiceAsPaid = (invoice: Pick<Invoice, 'type' | 'status' | 'paid' | 'amount'>): boolean =>
  invoice.type === InvoiceType.COMPANY &&
  PAYABLE_COMPANY_INVOICE_STATUSES.includes(invoice.status) &&
  !invoice.paid &&
  invoice.amount !== 0;

/** Movements to check on the invoice's account that pay it: incoming for an invoice, outgoing for a credit note. */
export const getMatchingBankMovements = <
  T extends Pick<
    TransactionWithoutPostings,
    'amount' | 'bankAccountId' | 'operationDate' | 'origin' | 'snoozedUntil' | 'status'
  >,
>(
  transactions: T[],
  signedInvoiceAmount: number,
  bankAccountId: string
): T[] =>
  orderBy(
    transactions.filter(
      (transaction) =>
        transaction.bankAccountId === bankAccountId &&
        transaction.origin !== TransactionOrigin.ASSUMED &&
        isTransactionToReconcile(transaction) &&
        roundAtSecondDecimal(transaction.amount) === roundAtSecondDecimal(signedInvoiceAmount)
    ),
    'operationDate',
    'desc'
  );

/** Movements that marking the invoice paid excluded: same account and amount, comment ending with its number. */
export const getMovementsExcludedForInvoice = <
  T extends Pick<TransactionWithoutPostings, 'amount' | 'bankAccountId' | 'exclusionComment'>,
>(
  excludedTransactions: T[],
  invoice: Pick<Invoice, 'bankAccountId' | 'invoiceNumber'>,
  signedInvoiceAmount: number
): T[] => {
  const { bankAccountId, invoiceNumber } = invoice;
  if (isNilOrEmpty(invoiceNumber)) {
    return [];
  }
  return excludedTransactions.filter(
    (transaction) =>
      transaction.bankAccountId === bankAccountId &&
      roundAtSecondDecimal(transaction.amount) === roundAtSecondDecimal(signedInvoiceAmount) &&
      (transaction.exclusionComment ?? '').endsWith(` ${invoiceNumber}`)
  );
};

export const getBankMovementLabelValues = (
  {
    operationDate,
    counterpartName,
    amount,
  }: Pick<TransactionWithoutPostings, 'amount' | 'counterpartName' | 'operationDate'>,
  language: Language
) => ({
  date: isNil(operationDate) ? '' : formatCalendarDate(calendarDateFromTimestamp(operationDate), language),
  counterpart: counterpartName ?? '',
  amount: formatNumberToEuroCurrency(amount, language),
});

export interface CompanyInvoicePeppolParty {
  recipient?: Contact | null;
  sender?: Contact | null;
}

export interface CompanyInvoicePeppolEligibility {
  isPeppolChannelVisible: boolean;
  canSendPeppol: boolean;
  peppolError: PeppolErrorType | null;
  contactIdsToVerify: string[];
}

// Nothing a user can do on an invoice fixes these two, so the channel stays out of the dialog
// instead of showing a reason nobody can act on.
const HIDDEN_PEPPOL_ERRORS: PeppolErrorType[] = [
  PeppolErrorType.INTEGRATION_NOT_ACTIVE,
  PeppolErrorType.RECIPIENT_NOT_COMPANY,
];

export const getCompanyInvoicePeppolEligibility = (
  parties: CompanyInvoicePeppolParty[],
  verifiedContactIds: string[],
  peppolIntegration?: Integration
): CompanyInvoicePeppolEligibility => {
  const partyErrors = compact(
    parties.map(({ recipient, sender }) => {
      if (isNil(recipient)) {
        return PeppolErrorType.RECIPIENT_NOT_COMPANY;
      }
      return getPeppolError(
        [withVerifiedRegistration(recipient, verifiedContactIds)],
        isNil(sender) ? sender : withVerifiedRegistration(sender, verifiedContactIds),
        peppolIntegration
      );
    })
  );
  // One party nobody can do anything about must not bury an error another party can act on, so the
  // actionable one is what the dialog reports and what decides whether the channel is shown at all.
  const peppolError = partyErrors.find((partyError) => !HIDDEN_PEPPOL_ERRORS.includes(partyError)) ?? partyErrors[0];
  const isPeppolChannelVisible =
    !isEmpty(parties) && (isNil(peppolError) || !HIDDEN_PEPPOL_ERRORS.includes(peppolError));
  // Verifying a contact writes to it, so it is only ever asked for when a registration is the one
  // thing still standing between this invoice and a Peppol send.
  const isRegistrationWorthVerifying = isNil(peppolError) || VERIFIABLE_PEPPOL_ERRORS.includes(peppolError);

  return {
    isPeppolChannelVisible,
    canSendPeppol: !isEmpty(parties) && isNil(peppolError),
    peppolError: peppolError ?? null,
    contactIdsToVerify: isRegistrationWorthVerifying
      ? uniq(
          compact(parties.flatMap(({ recipient, sender }) => [recipient, sender]))
            .filter(isPeppolRegistrationUnverified)
            .map((contact) => contact.id)
        )
      : [],
  };
};
